import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, Inject, Input
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  takeUntil
} from 'rxjs';
import { ConfigurationService, IProviderShareableLinkAppConfig } from '@sondermind/configuration';
import { EmptyURIValue, URIPrefix } from '@sondermind/data-access/directory-link';
import { NavigationKeys } from '@sondermind/iris/dropdown';
import { IconColors, IconSizes } from '@sondermind/iris/icon';
import { LAUNCH_DARKLY_SERVICE, LDBooleanFeatureFlags, LaunchDarklyService } from '@sondermind/launch-darkly';
import { BreakpointsService } from '@sondermind/utilities/services';
import { WindowRefService } from '@sondermind/utilities/window';

@Component({
  selector: 'public-header',
  templateUrl: './public-header.component.html',
  styleUrls: ['./public-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class PublicHeaderComponent {
  @Input() set providerPracticeState(state: string) {
    this.providerPracticeStateValue = state;
    this.buildFindProviderUrl();
  }
  providerPracticeStateValue = '';
  showMobileMenu: boolean = false;
  showExploreCareMenu: boolean = false;
  showLoginMenu: boolean = false;
  isMobile: boolean = false;
  findProviderUrl: string = '';
  ctaButtonText: string = '';
  destroyed$ = new Subject<void>();

  iconSizes = IconSizes;
  iconColors = IconColors;
  redirectToProviderDirectoryFeatureFlag: boolean = false;
  anonUserId: string = '';
  queryParams: Params | null = null;
  providerPortalLoginUrl = 'https://portal.sondermind.com/login/';
  clientPortalLoginUrl = 'https://client.sondermind.com/';

  getMatchedCtaEnabled$ = new BehaviorSubject<boolean>(false);

  constructor(
    public breakpointService: BreakpointsService,
    private route: ActivatedRoute,
    @Inject(LAUNCH_DARKLY_SERVICE)
    private ldService: LaunchDarklyService,
    private configurationService: ConfigurationService<IProviderShareableLinkAppConfig>,
    private windowRef: WindowRefService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.setLoginUrls();
    combineLatest([
      this.checkLdFlags(),
      this.route.queryParams
    ])
      .pipe(takeUntil(this.destroyed$)).subscribe(([enabled, queryParams]) => {
        this.redirectToProviderDirectoryFeatureFlag = enabled;
        if (queryParams['anonUserId']) {
          this.anonUserId = queryParams['anonUserId'] as string;
        }

        this.getMatchedCtaEnabled$.next(queryParams['showGetMatchedCta'] === 'true');

        this.queryParams = queryParams;
        this.buildFindProviderUrl();
      });
    this.breakpointService.isMobileOrTablet$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((isMobileOrTablet) => {
      this.isMobile = isMobileOrTablet;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
  setLoginUrls(): void {
    this.providerPortalLoginUrl = this.configurationService.env.providerPortalBase ?
      `${this.configurationService.env.providerPortalBase}/login/` : 'https://portal.sondermind.com/login/';

    this.clientPortalLoginUrl = this.configurationService.env.clientPortalBase ?
      `${this.configurationService.env.clientPortalBase}` : 'https://client.sondermind.com/';
  }

  buildFindProviderUrl(): void {
    const isLinkToProviderDirectory = this.providerPracticeStateValue?.length > 0;

    if (this.redirectToProviderDirectoryFeatureFlag && isLinkToProviderDirectory) {
      this.findProviderUrl = this.buildProviderDirectoryUrl();
    } else {
      this.findProviderUrl = this.buildMatchFlowUrl();
    }

    if (this.anonUserId) {
      this.findProviderUrl = `${this.findProviderUrl}&anonUserId=${this.anonUserId}`;
    }

    this.ctaButtonText = isLinkToProviderDirectory ? 'Find a provider' : 'Get matched with a provider';
  }

  setShowLoginMenu(val: boolean): void {
    this.showLoginMenu = val;
  }

  private buildProviderDirectoryUrl(): string {
    const referringUrl = this.queryParams?.['referringUrl'] as string
      ?? encodeURIComponent(this.windowRef.nativeWindow?.location.host || 'localhost:4206');

    const providerDirectoryRoot = this.configurationService.env.providerDirectoryBase
                                  ?? 'https://www.sondermind.com/provider-directory';

    const params = `referringUrl=${referringUrl}`;

    const stateUrl = `${URIPrefix.STATE}-${this.providerPracticeStateValue
      ? this.providerPracticeStateValue.toLocaleLowerCase() : EmptyURIValue}`;

    const cityUrl = `${URIPrefix.CITY}-${EmptyURIValue}`;
    return `${providerDirectoryRoot}/${stateUrl}/${cityUrl}?${params}`;
  }

  private buildMatchFlowUrl(): string {
    const matchFlowRoot = this.configurationService.env.app?.flowHost
    ?? 'https://start.sondermind.com/';

    const params = `referringUrl=${encodeURIComponent(this.windowRef.nativeWindow?.location.href || 'localhost:4203')}`;

    return `${matchFlowRoot}?${params}`;
  }

  checkLdFlags(): Observable<boolean> {
    return this.ldService.checkFlagEnabled$(LDBooleanFeatureFlags.FIND_PROVIDER_PUBLIC_HEADER_REDIRECT);
  }

  toggleMobileMenu(): void {
    this.showMobileMenu = !this.showMobileMenu;
  }

  toggleMobileMenuKeyDown(e: KeyboardEvent): void {
    if (e.key === NavigationKeys[' '] || e.key === NavigationKeys.Enter) {
      this.toggleMobileMenu();
    }
  }

  toggleExploreCare(): void {
    this.showExploreCareMenu = !this.showExploreCareMenu;
  }

  setExploreCare(showMenu: boolean): void {
    this.showExploreCareMenu = showMenu;
  }
}
